import React from "react";
import { graphql } from "react-relay";

import cx from "classnames";
import { noop } from "lodash";
import { Node as ProseNode } from "prosemirror-model";
import { EditorState } from "prosemirror-state";

import { EditorConfig, Extension, ProseReact } from "~/lib/prose";
import { getAllMarks } from "~/lib/prose/marks";
import {
	BulletPoint,
	CustomEmoji,
	Embed,
	HardBreak,
	Header as HeaderNode,
	HorizontalRule,
	KeyResult,
	Mention,
	NumberedListItem,
	Tag,
	TextNode,
	Unstyled,
} from "~/lib/prose/nodes";
import { ImageStrip, ImageStripItem, ImageStripItemCaption, ImageStripItemFile } from "~/lib/prose/nodes/image-strip";
import {
	Agenda,
	DropTarget,
	Goal,
	Indent,
	InputRules,
	MoveSelection,
	MultiSelect,
	MultiSelectDropHandler,
	Placeholder,
	SlashProvider,
	Todo,
	TypeAhead,
} from "~/lib/prose/plugins";
import BlockBoundary from "~/lib/prose/plugins/blockBoundary";
import { FileDropHandler, NoteTaskDropHandler } from "~/lib/prose/plugins/dropTarget";
import EmojiProvider, { PasteBuiltInEmoji } from "~/lib/prose/plugins/emoji";
import MousePos from "~/lib/prose/plugins/mousePos";
import { PromptExtension } from "~/lib/prose/plugins/prompt";
import Toolbar from "~/lib/prose/plugins/toolbar";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { withSuspense } from "~/lib/suspense";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import AppContext from "../common/AppContext";

import type { contentNoteTemplateQuery } from "~/__generated__/contentNoteTemplateQuery.graphql";

import styles from "./css/content.module.css";

export type DocumentJSON = { [key: string]: unknown };

const toolbarMountPointElementId = "template-editor-toolbar-mount-point";

export const getTemplateContentEditorConfig = (schemaVersion: number, editable = false, disablePrompt = false) => {
	let extensions: Extension[] = [
		new BlockBoundary(),
		...getAllMarks({ linkEditable: editable, placeholderEditable: editable, disableLinkPill: schemaVersion < 2 }),
		new TextNode(),
		new Indent(),
		new Unstyled({ showBullet: schemaVersion < 2 }),
		...(schemaVersion >= 2 ? [new BulletPoint(), new NumberedListItem()] : []),
		...(schemaVersion >= 3 ? [new HorizontalRule()] : []),
		new HardBreak(),
		new HeaderNode({ inTemplate: true }),
		new ImageStrip({ contentId: "", disable: !editable }),
		new ImageStripItem(),
		new ImageStripItemFile(),
		new ImageStripItemCaption(),
		new Tag({ showToolbar: false, clickable: false }),
		new Todo({ disableJira: true, localToggle: true, disabledReadOnly: true }),
		new Agenda({ localToggle: true, disabledReadOnly: true }),
		new Goal(),
		new Mention(),
		new Placeholder(),
		new Embed(),
		new KeyResult(),
		...(!disablePrompt ? [new PromptExtension({ inTemplate: true })] : []),
		...(schemaVersion >= 2 && window.INITIAL_STATE.account?.relay_id ? [new CustomEmoji()] : []),
		...(schemaVersion >= 2 ? [new PasteBuiltInEmoji()] : []),
	];

	if (editable) {
		extensions = extensions.concat([
			new Toolbar({
				mountPointId: toolbarMountPointElementId,
				noJira: true,
				noMention: true,
			}),
			new MoveSelection(),
			new DropTarget({
				handlers: [new NoteTaskDropHandler(), new MultiSelectDropHandler(), new FileDropHandler(noop)],
			}),
			new MultiSelect({ selectionTabClass: cx("multiselect-drag-handle", styles.blockDragHandle) }),
			new MousePos(),
			new TypeAhead({
				providers: [
					new SlashProvider({
						exclude: [
							"Jira",
							"ClickUp",
							"Trello",
							"Asana",
							"monday.com",
							"Linear",
							"Microsoft To Do",
							"Key result",
							schemaVersion === 1 ? "Numbered List" : "",
						],
					}),
					new EmojiProvider(),
				],
			}),
			new InputRules(),
		]);
	}

	return new EditorConfig({
		extensions,
		nodes: {
			doc: {
				content: "block+",
			},
		},
	});
};

const noteTemplateContentQuery = graphql`
	query contentNoteTemplateQuery($templateId: ID!) {
		node(id: $templateId) {
			... on NoteTemplateNode {
				proseDoc
				schemaVersion
			}
		}

		account {
			billingAccount {
				hasAccessToPromptHeadings: hasAccessToFeature(feature: prompt_headings)
			}
		}
	}
`;

interface BaseProps {
	proseReactClassName?: string;
	containerClassName?: string;
	editable?: boolean;
	onChange?: (proseJSON: DocumentJSON) => void;
}

function LoadingFallback() {
	return (
		<div className={styles.loaderContainer}>
			<LoadingSpinner inline="centered" delay={500} />
		</div>
	);
}

export const NoteTemplateContentRenderer = withSuspense(<LoadingFallback />, NoteTemplateContentRendererInner);

function NoteTemplateContentRendererInner({
	templateId,
	readOnly = false,
	...baseProps
}: { templateId: string; readOnly?: boolean } & BaseProps) {
	const props = useLazyLoadQuery<contentNoteTemplateQuery>(
		noteTemplateContentQuery,
		{ templateId },
		{ fetchPolicy: "network-only" },
	);

	const schemaVersion = props.node?.schemaVersion;
	const document = props.node?.proseDoc;

	if (!props.node) throw new Error("You don't have access to this template.");
	const editorConfig = React.useMemo(
		() => getTemplateContentEditorConfig(schemaVersion || 1, !readOnly),
		[readOnly, schemaVersion],
	);
	return (
		<NoteTemplateContent
			document={document}
			editorConfig={editorConfig}
			hasAccessToPromptHeadings={props.account.billingAccount.hasAccessToPromptHeadings}
			{...baseProps}
		/>
	);
}

const proseReactContext = { hideAssignableTools: true, inTemplate: true };

export const NoteTemplateContent: React.FC<
	{
		document: DocumentJSON;
		hasAccessToPromptHeadings: boolean;
		editorConfig: EditorConfig;
		extraContext?: { [key: string]: unknown };
	} & BaseProps
> = ({
	proseReactClassName,
	containerClassName,
	editorConfig,
	document,
	editable,
	onChange,
	hasAccessToPromptHeadings,
	extraContext,
}) => {
	const onContentChange = React.useCallback(
		(editorState: EditorState, changed: boolean) => {
			if (editable && changed) {
				onChange?.(editorState.doc.toJSON());
			}
		},
		[editable, onChange],
	);

	const { user } = React.useContext(AppContext);

	const editorContext = {
		...proseReactContext,
		...extraContext,
		participants: [user].filter(Boolean),
		currentUser: user,
		hasAccessToPromptHeadings,
	};

	return editable ? (
		<div className={cx(styles.editor, containerClassName)}>
			<div id={toolbarMountPointElementId} className={styles.editortoolbarMountPoint} />

			<ProseReact
				enableReactHost
				className={proseReactClassName}
				context={editorContext}
				config={editorConfig}
				onChange={onContentChange}
				initialDoc={editable ? document : undefined}
				controlledDoc={editable ? undefined : ProseNode.fromJSON(editorConfig.schema, document)}
				editable={!!editable}
			/>
		</div>
	) : (
		<ProseReact
			enableReactHost
			className={proseReactClassName}
			context={editorContext}
			config={editorConfig}
			onChange={onContentChange}
			initialDoc={editable ? document : undefined}
			controlledDoc={editable ? undefined : ProseNode.fromJSON(editorConfig.schema, document)}
			editable={!!editable}
		/>
	);
};
